import '@angular/compiler';

import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import localeNl from '@angular/common/locales/nl';
import localeDe from '@angular/common/locales/de';
import {
  enableProdMode,
  importProvidersFrom,
  inject,
  isDevMode,
  provideAppInitializer,
} from '@angular/core';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular';

import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { bootstrapApplication } from '@angular/platform-browser';
import {
  PreloadAllModules,
  provideRouter,
  RouteReuseStrategy,
  withComponentInputBinding,
  withNavigationErrorHandler,
  withPreloading,
  withRouterConfig,
} from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { IonicStorageModule, Storage } from '@ionic/storage-angular';
import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { authInterceptor } from './app/providers/auth.interceptor';
import { reloadOnStaleBuild } from './app/providers/stale-build';
import { AuthService } from './app/providers/auth.service';
import { UserService } from './app/providers/user.service';
import { environment } from './environments/environment';

if (environment.production) {
  enableProdMode();
}

// Register locale data so DatePipe/number formatting can localize per language.
registerLocaleData(localeEs);
registerLocaleData(localeFr);
registerLocaleData(localeNl);
registerLocaleData(localeDe);

bootstrapApplication(AppComponent, {
  providers: [
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    // Lets Ionic pass overlay componentProps through ComponentRef.setInput, so pages can
    // read them as signal inputs instead of the deprecated NavParams.
    provideIonicAngular({ useSetInputAPI: true }),
    provideRouter(
      routes,
      withPreloading(PreloadAllModules),
      withComponentInputBinding(),
      // Inherit parent route `data` (e.g. SEO metadata) into child routes so a
      // shell route's `data.seo` reaches its lazily-loaded children.
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
      // A page script deleted by a newer deploy: load that page fresh.
      withNavigationErrorHandler(reloadOnStaleBuild),
    ),
    provideHttpClient(withInterceptors([authInterceptor])),
    provideTranslateService({
      fallbackLang: 'en',
      lang: 'en',
      loader: provideTranslateHttpLoader({
        prefix: 'assets/i18n/',
        suffix: '.json',
      }),
    }),
    importProvidersFrom(IonicStorageModule.forRoot()),
    provideServiceWorker('ngsw-worker.js', {
      // Off in the desktop app: Electron serves the files itself, and a
      // service worker there would keep serving an old build after updates.
      enabled: !isDevMode() && !environment.desktop,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    // Read the stored session *before* the first render. Both the signed-in
    // flag and the API token live in Ionic Storage (IndexedDB), so resolving
    // them inside AppComponent.ngOnInit meant the first paint showed a
    // signed-out menu for ~200ms after every reload — long enough to see the
    // admin section pop in, with nothing highlighted until it did.
    provideAppInitializer(() => {
      // Every `inject()` has to run synchronously, before the first await:
      // an initializer loses its injection context as soon as it suspends.
      const storage = inject(Storage);
      const auth = inject(AuthService);
      const user = inject(UserService);
      return (async () => {
        await storage.create();
        await Promise.all([auth.restore(), user.isLoggedIn()]);
      })();
    }),
  ],
}).catch(err => console.error(err));
